import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Play, Square } from 'lucide-react';
import { API } from '@/App';

const VOICES = [
  { id: 'female', label: 'Féminine' },
  { id: 'male', label: 'Masculine' },
];

// Lets each admin pick the copilot voice and hear a short sample of it.
export const VoicePicker = () => {
  const [voice, setVoice] = useState(null);
  const [sample, setSample] = useState('idle');
  const audioRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/copilot/voice`).then(r => setVoice(r.data.voice)).catch(() => {});
    return () => audioRef.current?.pause();
  }, []);

  const choose = async (v) => {
    if (v === voice) return;
    audioRef.current?.pause();
    setSample('idle');
    const prev = voice;
    setVoice(v);
    try {
      await axios.put(`${API}/copilot/voice`, { voice: v });
      toast.success(`Voix ${v === 'male' ? 'masculine' : 'féminine'} choisie pour vos réponses lues`);
    } catch {
      setVoice(prev);
      toast.error("Impossible d'enregistrer votre choix de voix");
    }
  };

  const playSample = async () => {
    if (sample === 'playing') { audioRef.current?.pause(); setSample('idle'); return; }
    setSample('loading');
    try {
      const res = await axios.get(`${API}/copilot/voice/sample/${voice}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = audioRef.current || (audioRef.current = new Audio());
      a.src = url;
      a.onended = () => { setSample('idle'); URL.revokeObjectURL(url); };
      await a.play();
      setSample('playing');
    } catch {
      setSample('idle');
      toast.error("Impossible de lire l'exemple de voix");
    }
  };

  if (!voice) return null;
  return (
    <div className="flex items-center gap-1.5" data-testid="copilot-voice-picker">
      <span className="text-[11px] text-gray-500 hidden sm:inline">Voix</span>
      <div className="flex rounded-full border border-gray-200 p-0.5 bg-gray-50" role="radiogroup" aria-label="Voix du copilote">
        {VOICES.map(v => (
          <button key={v.id} role="radio" aria-checked={voice === v.id} onClick={() => choose(v.id)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${voice === v.id ? 'bg-white text-[#059669] shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
            data-testid={`copilot-voice-${v.id}`}>
            {v.label}
          </button>
        ))}
      </div>
      <button onClick={playSample} disabled={sample === 'loading'} title="Écouter un exemple"
        className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:text-[#059669] hover:border-[#059669] transition-colors disabled:opacity-50"
        data-testid="copilot-voice-sample-btn">
        {sample === 'loading' ? <Loader2 size={12} className="animate-spin" /> : sample === 'playing' ? <Square size={11} /> : <Play size={12} />}
      </button>
    </div>
  );
};
